import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import confirm from 'components/confirm';
import { faRotate } from '@fortawesome/pro-solid-svg-icons';

import ContactDetails from 'bundles/backoffice/contacts/details';
import ContactIcon from 'bundles/backoffice/contacts/icon';
import Dropdown from 'components/dropdown';
import ReplaceContactModal from 'bundles/backoffice/contacts/replace_modal';
import { clickAsLink } from 'helpers/link_helpers';

export function Contact({
  confirmDeletion,
  contactRole,
  externalEditing,
  formModalComponent,
  forRecord,
  readOnly,
  renderLinks,
  showEmployer = true,
  onDelete,
  onReplace,
  onSave,
}) {
  const [showContactEditModal, setShowContactEditModal] = useState();
  const [showBusinessEditModal, setShowBusinessEditModal] = useState();
  const [showReplaceContactModal, setShowReplaceContactModal] = useState();

  const contact = useMemo(() => contactRole.contact, [contactRole]);

  let business;
  if (showEmployer) {
    business = contact.business;
  }

  const FormModal = formModalComponent;

  function handleEdit(event) {
    if (externalEditing) {
      clickAsLink(window, contact.path, event);
    } else {
      setShowContactEditModal(true);
    }
  }

  function handleBusinessEdit(event) {
    if (externalEditing) {
      clickAsLink(window, business.path, event);
    } else {
      setShowBusinessEditModal(true);
    }
  }

  function deleteContact() {
    if (confirmDeletion) {
      confirm('Are you sure you want to remove this contact?', {
        buttonLabel: 'Remove',
        buttonStyle: 'danger',
        title: `Remove Contact`
      }).then(() => {
        onDelete(contactRole);
      });
    } else {
      onDelete(contactRole);
    }
  }

  return (
    <div className="contact d-flex gap-2 py-4 justify-content-center w-100">
      <ContactIcon contact={contact} />

      <ContactDetails
        contact={contact}
        contactRole={contactRole}
        renderLinks={renderLinks}
        showEmployer={showEmployer}
      />

      {!readOnly &&
        <Dropdown align="end">
          <Dropdown.MenuItem isEdit onClick={handleEdit} />
          {business && <Dropdown.MenuItem isEdit onClick={handleBusinessEdit} />}
          <Dropdown.MenuItem icon={faRotate} label="Replace" onClick={() => setShowReplaceContactModal(true)}/>
          <Dropdown.MenuItem isDelete label="Remove" onClick={deleteContact} />
        </Dropdown>
      }

      <FormModal
        contact={contact}
        forRecord={forRecord}
        show={showContactEditModal}
        onHide={() => setShowContactEditModal(false)}
        onSave={onSave}
      />

      <FormModal
        contact={business || {}}
        forRecord={forRecord}
        show={showBusinessEditModal}
        onHide={() => setShowBusinessEditModal(false)}
        onSave={onSave}
      />

      <ReplaceContactModal
        contactRole={contactRole}
        newContactVisibility="shared"
        show={showReplaceContactModal}
        onChange={newContactRole => onReplace(contactRole, newContactRole)}
        onHide={() => setShowReplaceContactModal(false)}
      />
    </div>
  );
}

Contact.propTypes = {
  confirmDeletion: PropTypes.bool,
  contactRole: PropTypes.shape({
    contact: PropTypes.shape({
      business: PropTypes.shape({
        name: PropTypes.string,
      }),
      isBusiness: PropTypes.bool,
      email: PropTypes.string,
      name: PropTypes.string,
      path: PropTypes.string,
      phoneFormatted: PropTypes.string,
      licenseNumebr: PropTypes.string,
    }).isRequired,
  }),
  externalEditing: PropTypes.bool,
  formModalComponent: PropTypes.func.isRequired,
  forRecord: PropTypes.object,
  readOnly: PropTypes.bool,
  renderLinks: PropTypes.bool,
  showEmployer: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onSave: PropTypes.func,
};

export default Contact;
